import { useGlobalContext } from '~/context/GlobalContext';
import { useAuth } from '~/hooks/useAuth';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from '~/hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import { authStorage } from '~/services/authStorage';
import classNames from 'classnames';
import TopBarContainer from '~/components/TopBarContainer/TopBarContainer';
import Logo from '~/components/Logo/Logo';
import RegisterNickModal from './RegisterNickModal';
import { openAvatarLightbox } from '~/components/AvatarLightbox/openAvatarLightbox';
import styles from './HeaderContainer.module.css';

function HeaderContainer() {
  const { context } = useGlobalContext();
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<'none' | 'options' | 'profile'>('none');
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  const isCustomNickGuest =
    !context.authUser &&
    !!context?.user?.name &&
    !context.user.name.startsWith('Gość_');

  // --- LOGIKA DŹWIĘKU: Inicjalizacja z localStorage ---
  const [isSoundOn, setIsSoundOn] = useState(() => {
    const stored = localStorage.getItem('sound');
    return stored !== null ? stored === 'true' : true;
  });

  // --- NOWE: LOGIKA WIBRACJI: Inicjalizacja z localStorage ---
  const [isVibrationOn, setIsVibrationOn] = useState(() => {
    const stored = localStorage.getItem('vibration');
    return stored !== null ? stored === 'true' : true;
  });

  const [rememberMe, setRememberMe] = useState(() => authStorage.isRememberMeEnabled());

  // --- LOGIKA ZAMYKANIA POZA OBSZAREM ---
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        activeSection !== 'none' &&
        headerRef.current &&
        !headerRef.current.contains(event.target as Node)
      ) {
        setActiveSection('none');
      }
    };

    if (activeSection !== 'none') {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [activeSection]);

  const onClickHandle = () => {
    // Całkowite wylogowanie: serwer + WebRTC + localStorage/sessionStorage + stan (useAuth.logout()).
    logout();
  };

  const toggleSection = (section: 'options' | 'profile') => {
    setActiveSection((prev) => (prev === section ? 'none' : section));
  };

  const getAvatarSrc = (gender?: 'male' | 'female' | string) => {
    if (gender === 'female') return '/img/common/female-white.svg';
    return '/img/common/male-white.svg';
  };

  const authAvatarUrl = context.authUser?.avatarUrl || null;
  const displayName = context.authUser?.nick || context?.user?.name || 'Gość';

  const handleAvatarClick = () => {
    if (!context.authUser) return; // tylko zarejestrowany ma galerię
    setActiveSection('none');
    openAvatarLightbox({ userName: context.authUser.nick, avatarUrl: authAvatarUrl });
  };

  const handleRememberMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setRememberMe(newValue);
    authStorage.setRememberMe(newValue);
  };

  // --- OBSŁUGA ZMIANY DŹWIĘKU ---
  const handleSoundChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStatus = e.target.checked;
    setIsSoundOn(newStatus);
    localStorage.setItem('sound', String(newStatus));
    
    window.dispatchEvent(new CustomEvent('eventAudio', {
      bubbles: true,
      detail: { sound_chat: newStatus }
    }));
  };

  // --- NOWE: OBSŁUGA ZMIANY WIBRACJI ---
  const handleVibrationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStatus = e.target.checked;
    setIsVibrationOn(newStatus);
    localStorage.setItem('vibration', String(newStatus));
    
    // Wysyłamy ten sam event co dźwięk, ale z polem vibration_chat
    window.dispatchEvent(new CustomEvent('eventAudio', {
      bubbles: true,
      detail: { vibration_chat: newStatus }
    }));
  };

  useEffect(() => {
    const visualViewport = window.visualViewport;
    if (!visualViewport) return;

    const handleViewportChange = () => {
      if (headerRef.current) {
        const offset = visualViewport.offsetTop;
        headerRef.current.style.transform = `translateY(${offset}px)`;
      }
    };

    visualViewport.addEventListener('resize', handleViewportChange);
    visualViewport.addEventListener('scroll', handleViewportChange);

    return () => {
      visualViewport.removeEventListener('resize', handleViewportChange);
      visualViewport.removeEventListener('scroll', handleViewportChange);
    };
  }, []);

  return (
    <header
      data-component="HeaderContainer"
      ref={headerRef}
      className={styles.headerContainer}>
      <div className={styles.headerContainerTop}>
        <Logo />
        <div className={styles.headerContainerButton}>

          <div onClick={toggleTheme} className={styles.theme}>
            {theme === 'dark' ? '🌙' : '☀️'}
          </div>

          {context.authUser?.role === 'administrator' && (
            <button
              onClick={() => navigate('/admin')}
              className={classNames(styles.headerButton, styles.headerButtonAdmin)}
              title="Panel administratora"
              type="button"
            >
              <svg  className={classNames(styles.headerButtonSvg)} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7l-9-5z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </button>
          )}

          {(context.authUser?.role === 'moderator' || context.authUser?.role === 'administrator') && (
            <button
              onClick={() => navigate('/moderator')}
              className={classNames(styles.headerButton, styles.headerButtonAdmin)}
              title="Panel moderatora"
              type="button"
            >
              <svg className={classNames(styles.headerButtonSvg)} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7l-9-5z" />
                <circle cx="12" cy="11" r="2" />
                <path d="M8 16c0-2 2-3 4-3s4 1 4 3" />
              </svg>
            </button>
          )}

          <button
            onClick={() => toggleSection('profile')}
            className={classNames(styles.headerButton, styles.headerButtonProfile, {
              [styles.active]: activeSection === 'profile',
              [styles.headerButtonProfilePhoto]: !!authAvatarUrl,
            })}
            style={authAvatarUrl ? { backgroundImage: `url("${authAvatarUrl}")` } : undefined}
            type="button"
            aria-label="Menu profilu"
          />

        </div>
      </div>

      <div className={styles.headerContainerContent}>
        {/* DYMEK PROFIL */}
        <div className={classNames(styles.chatBubble, styles.bubbleProfile, {
          [styles.bubbleActive]: activeSection === 'profile'
        })}>
          <div className={classNames(styles.bubbleTail, styles.bubbleTailProfile)} />

          <div className={styles.profileHeader}>
            <div
              className={styles.avatarMini}
              onClick={handleAvatarClick}
              title={authAvatarUrl ? 'Pokaż moje zdjęcia' : ''}
              role={context.authUser ? 'button' : undefined}
            >
              {authAvatarUrl ? (
                <img className={styles.avatarMiniPhoto} src={authAvatarUrl} alt={displayName} />
              ) : (
                <img className={styles.avatarMiniImg} src={getAvatarSrc(context?.user?.gender)} alt="avatar" />
              )}
            </div>
            <div className={styles.profileMeta}>
              <div className={styles.name}>{displayName}</div>
              <div className={styles.gender}>{context?.user?.gender === 'male' ? 'Mężczyzna' : 'Kobieta'}</div>
              <div className={styles.status}>🟢 Dostępny</div>
            </div>
          </div>

          <div className={styles.options}>
            <label htmlFor="sound_chat" className={styles.label}>Dźwięk czatu</label>
            <input
              type="checkbox"
              id="sound_chat"
              className={styles.bubbleCheck}
              checked={isSoundOn}
              onChange={handleSoundChange}
            />
          </div>

          {/* NOWE: Opcja wibracji w dymku profilu */}
          <div className={styles.options}>
            <label htmlFor="vibration_chat" className={styles.label}>Wibracje czatu</label>
            <input
              type="checkbox"
              id="vibration_chat"
              className={styles.bubbleCheck}
              checked={isVibrationOn}
              onChange={handleVibrationChange}
            />
          </div>

          {context.authUser && (
            <div className={styles.options}>
              <label htmlFor="remember_me_chat" className={styles.label}>Nie wylogowywuj mnie</label>
              <input
                type="checkbox"
                id="remember_me_chat"
                className={styles.bubbleCheck}
                checked={rememberMe}
                onChange={handleRememberMeChange}
              />
            </div>
          )}

          <div className={styles.actions}>
            {isCustomNickGuest && (
              <button
                onClick={() => {
                  setIsRegisterOpen(true);
                  setActiveSection('none');
                }}
                className={classNames(styles.btnAction, styles.btnRegister)}
              >
                Zarejestruj nick
              </button>
            )}
            {context.authUser && (
              <button
                onClick={() => {
                  setActiveSection('none');
                  navigate('/profile');
                }}
                className={classNames(styles.btnAction, styles.btnProfile)}
              >
                Profil użytkownika
              </button>
            )}
            <button onClick={onClickHandle} className={classNames(styles.btnAction, styles.btnLogout)}>Wyloguj</button>
          </div>

        </div>
      </div>

      <TopBarContainer />

      <RegisterNickModal
        isOpen={isRegisterOpen}
        onClose={() => setIsRegisterOpen(false)}
        defaultNick={context?.user?.name || ''}
        defaultGender={context?.user?.gender || ''}
        chatUserId={context?.user?.id}
      />
    </header>
  );
}

export default HeaderContainer;